import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const CookieBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const { language } = useLanguage();
  const isGreek = language === 'el';

  const copy = {
    title: isGreek ? 'Χρήση Cookies' : 'Cookie usage',
    description: isGreek
      ? 'Αυτός ο ιστότοπος χρησιμοποιεί cookies για να βελτιώσει την εμπειρία σας και να αναλύσει την επισκεψιμότητα μέσω Google Analytics και Meta Pixel. Τα δεδομένα συλλέγονται ανώνυμα και δεν μοιράζονται με τρίτους. Συνεχίζοντας την περιήγηση, συμφωνείτε με τη χρήση cookies.'
      : 'This website uses cookies to improve your experience and analyze traffic via Google Analytics and the Meta Pixel. Data is collected anonymously and not shared with third parties. By continuing to browse, you agree to the use of cookies.',
    learnMore: isGreek ? 'Μάθετε περισσότερα' : 'Learn more',
    settings: isGreek ? 'Ρυθμίσεις' : 'Settings',
    necessaryOnly: isGreek ? 'Μόνο Απαραίτητα' : 'Necessary only',
    acceptAll: isGreek ? 'Αποδοχή Όλων' : 'Accept all',
  };

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('pixelpro-cookie-consent');
    if (!cookieConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('pixelpro-cookie-consent', 'accepted');
    localStorage.setItem('pixelpro-analytics-consent', 'true');
    localStorage.setItem('pp_consent_v2', 'granted');
    setIsVisible(false);
    // Enable Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
    window.metaPixelConsent?.(true);
  };

  const acceptNecessary = () => {
    localStorage.setItem('pixelpro-cookie-consent', 'necessary-only');
    localStorage.setItem('pixelpro-analytics-consent', 'false');
    localStorage.setItem('pp_consent_v2', 'denied');
    setIsVisible(false);
    // Disable Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
    window.metaPixelConsent?.(false);
  };

  const saveSettings = (analytics: boolean) => {
    localStorage.setItem('pixelpro-cookie-consent', 'custom');
    localStorage.setItem('pixelpro-analytics-consent', analytics.toString());
    localStorage.setItem('pp_consent_v2', analytics ? 'granted' : 'denied');
    setIsVisible(false);
    setShowSettings(false);
    
    // Update Google Analytics consent
    if (typeof gtag !== 'undefined') {
      gtag('consent', 'update', {
        'analytics_storage': analytics ? 'granted' : 'denied'
      });
    }
    window.metaPixelConsent?.(analytics);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
      <div className="bg-black backdrop-blur-sm border border-white/20 rounded-lg shadow-lg max-w-4xl mx-auto">
        {!showSettings ? (
          // Main banner
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-bold text-white mb-2">{copy.title}</h3>
              <button
                onClick={() => setIsVisible(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <p className="text-gray-300 text-sm leading-relaxed mb-6">
              {copy.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
              <div className="flex flex-wrap gap-2 text-sm">
                <button
                  onClick={() => window.navigateToCookiePolicy?.()}
                  className="text-white hover:text-gray-300 underline"
                >
                  {copy.learnMore}
                </button>
                <span className="text-gray-500">|</span>
                <button
                  onClick={() => setShowSettings(true)}
                  className="text-white hover:text-gray-300 underline"
                >
                  {copy.settings}
                </button>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={acceptNecessary}
                  className="px-4 py-2 bg-black border border-white text-white text-sm rounded-lg hover:bg-white/10 transition-colors"
                >
                  {copy.necessaryOnly}
                </button>
                <button
                  onClick={acceptAll}
                  className="px-4 py-2 bg-white text-black text-sm rounded-lg font-medium transition-all duration-200 hover:bg-gray-200"
                >
                  {copy.acceptAll}
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Settings panel
          <CookieSettings 
            onSave={saveSettings}
            onBack={() => setShowSettings(false)}
          />
        )}
      </div>
    </div>
  );
};

interface CookieSettingsProps {
  onSave: (analytics: boolean) => void;
  onBack: () => void;
}

const CookieSettings: React.FC<CookieSettingsProps> = ({ onSave, onBack }) => {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const { language } = useLanguage();
  const isGreek = language === 'el';

  const copy = {
    title: isGreek ? 'Ρυθμίσεις Cookies' : 'Cookie settings',
    necessaryTitle: isGreek ? 'Απαραίτητα Cookies' : 'Necessary cookies',
    necessaryDescription: isGreek
      ? 'Αυτά τα cookies είναι απαραίτητα για τη βασική λειτουργία του ιστοτόπου και δεν μπορούν να απενεργοποιηθούν.'
      : 'These cookies are required for basic site functionality and cannot be disabled.',
    analyticsTitle: isGreek ? 'Αναλυτικά Cookies' : 'Analytics cookies',
    analyticsDescription: isGreek
      ? 'Βοηθούν να κατανοήσουμε πώς χρησιμοποιείτε τον ιστότοπο μέσω ανώνυμων στατιστικών (Google Analytics).'
      : 'Help us understand how you use the site through anonymous statistics (Google Analytics).',
    back: isGreek ? 'Πίσω' : 'Back',
    save: isGreek ? 'Αποθήκευση Ρυθμίσεων' : 'Save settings',
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white">{copy.title}</h3>
        <button
          onClick={onBack}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      <div className="space-y-4 mb-6">
        {/* Necessary Cookies */}
        <div className="flex items-start justify-between p-4 bg-black/40 rounded-lg border border-white/10">
          <div className="flex-1">
            <h4 className="font-semibold text-white mb-1">{copy.necessaryTitle}</h4>
            <p className="text-gray-300 text-sm">
              {copy.necessaryDescription}
            </p>
          </div>
          <div className="ml-4">
            <div className="w-12 h-6 bg-white rounded-full flex items-center justify-end px-1">
              <div className="w-4 h-4 bg-black rounded-full"></div>
            </div>
          </div>
        </div>

        {/* Analytics Cookies */}
        <div className="flex items-start justify-between p-4 bg-black/40 rounded-lg border border-white/10">
          <div className="flex-1">
            <h4 className="font-semibold text-white mb-1">{copy.analyticsTitle}</h4>
            <p className="text-gray-300 text-sm">
              {copy.analyticsDescription}
            </p>
          </div>
          <div className="ml-4">
            <button
              onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
              className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors border border-white ${
                analyticsEnabled ? 'bg-white justify-end' : 'bg-black justify-start'
              }`}
            >
              <div className={`w-4 h-4 rounded-full ${analyticsEnabled ? 'bg-black' : 'bg-white'}`}></div>
            </button>
          </div>
        </div>
      </div>
      
      <div className="flex gap-3 justify-end">
        <button
          onClick={onBack}
          className="px-4 py-2 bg-black border border-white text-white text-sm rounded-lg hover:bg-white/10 transition-colors"
        >
          {copy.back}
        </button>
        <button
          onClick={() => onSave(analyticsEnabled)}
          className="px-4 py-2 bg-white text-black text-sm rounded-lg font-medium transition-all duration-200 hover:bg-gray-200"
        >
          {copy.save}
        </button>
      </div>
    </div>
  );
};

export default CookieBanner;
